import { Constructor } from '@angular/cdk/table';
import { FormGroup } from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DigitalPublicationDataService } from 'src/app/content-management/shared/services/digital-publication-data.service';
import { DialogService } from '../services/dialog.service';
import {
  AutoCompleteData,
  IAutoCompleteData,
} from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';

type WithClientFormSectionType = {
  isSearchingClients: boolean;
  clientsSearchResult: IAutoCompleteData[];
  clientExactMatch: IAutoCompleteData;
  _initClient(client: string): void;
  _initClientValueChanges(getPreviousClient: () => string | null): void;
  _buildClient(): string;
  onAddNewClient(text: string): void;
};

type ClientFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: { CLIENT: () => string };
  destroy$: Subject<boolean>;
  digitalPublicationDataService: DigitalPublicationDataService;
  _dialogService: DialogService;
};

export function WithClientFormSection<
  TBase extends Constructor<ClientFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithClientFormSectionType;
  prototype: WithClientFormSectionType;
} & TBase {
  return class WithClientFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    isSearchingClients: boolean = false;
    clientsSearchResult: IAutoCompleteData[];
    clientExactMatch: IAutoCompleteData;

    _initClient(client: string): void {
      this.form
        .get(this.FORM_PATHS.CLIENT())
        .setValue(new AutoCompleteData(client, client));

      this.form
        .get(this.FORM_PATHS.CLIENT())
        .valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(500),
          tap(() => {
            this.isSearchingClients = true;
          }),
          switchMap((searchedValue: string) => {
            const getDataObs$ =
              searchedValue && typeof searchedValue === 'string'
                ? this.digitalPublicationDataService.searchClients$(
                    searchedValue
                  )
                : of(null);

            return getDataObs$.pipe(
              finalize(() => {
                this.isSearchingClients = false;
              })
            );
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.clientsSearchResult = response.results.map(
                (p) => new AutoCompleteData(p.text, p.text)
              );
              if (response.exactMatch) {
                this.clientExactMatch = new AutoCompleteData(
                  response.exactMatch.text,
                  response.exactMatch.text
                );
              } else {
                this.clientExactMatch = null;
              }
            } else {
              this.clientsSearchResult = null;
              this.clientExactMatch = null;
            }
          },
        });
    }

    _initClientValueChanges(getPreviousClient: () => string | null): void {
      this.form
        .get(this.FORM_PATHS.CLIENT())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue.displayName === getPreviousClient()) {
              this.form.get(this.FORM_PATHS.CLIENT()).markAsPristine();
            }
          },
        });
    }

    _buildClient(): string {
      return this.form.get(this.FORM_PATHS.CLIENT()).value.displayName;
    }

    onAddNewClient(text: string) {
      this.digitalPublicationDataService.createClient$(text).subscribe({
        next: (_) =>
          this.form
            .get(this.FORM_PATHS.CLIENT())
            .setValue(new AutoCompleteData(text, text)),
      });
    }
  };
}
